import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getBackendUrl } from '../config/apiConfig';
import SEO from './SEO';
import logger from '../utils/logger';
import './BlogView.css';

const fmtDate = (d) =>
  new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const readTime = (post) => {
  const words = (post.content || post.excerpt || '').split(/\s+/).length;
  return `${Math.max(1, Math.round(words / 200))} min`;
};

const BlogList = () => {
  const [searchParams] = useSearchParams();
  const activeTag = searchParams.get('tag');
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const url = activeTag
          ? `${getBackendUrl()}/api/blog?tag=${encodeURIComponent(activeTag)}&limit=50`
          : `${getBackendUrl()}/api/blog?limit=50`;
        const [postsRes, tagsRes] = await Promise.all([
          fetch(url),
          fetch(`${getBackendUrl()}/api/blog/tags`),
        ]);
        if (postsRes.ok) setPosts(await postsRes.json());
        if (tagsRes.ok) setTags(await tagsRes.json());
      } catch (err) {
        logger.error('Failed to load blog posts', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeTag]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return posts;
    return posts.filter(
      (p) =>
        (p.title || '').toLowerCase().includes(q) ||
        (p.excerpt || '').toLowerCase().includes(q) ||
        (p.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  }, [posts, query]);

  const recent = posts.slice(0, 5);

  return (
    <div className="blog-view">
      <SEO
        title={activeTag ? `Blog - ${activeTag}` : 'Blog'}
        description="Read articles about matchmaking, compatibility, and the L3V3L platform."
        keywords={activeTag
          ? `l3v3l matches blog, level matches blog, indian matrimony blog, ${activeTag}`
          : 'l3v3l matches blog, level matches blog, matrimony insights, indian matchmaking tips, matrimonial services blog'}
        url="/blog"
      />

      <div className="bl-top">
        <div>
          <h1 className="bv-title bl-title">📝 L3V3L Blog</h1>
          <p className="bl-sub">
            {activeTag ? `Posts tagged "${activeTag}"` : 'Tips, stories & updates'}
          </p>
        </div>
        <div className="bl-search">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles…"
            aria-label="Search articles"
          />
          <span className="bl-search-ico">🔍</span>
        </div>
      </div>

      {activeTag && (
        <Link to="/blog" className="bv-back-link" style={{ marginBottom: 16, display: 'inline-block' }}>
          ← Clear filter
        </Link>
      )}

      <div className="bl-layout">
        <div className="bl-tiles">
          {loading ? (
            <div className="blog-view-loading">Loading posts…</div>
          ) : filtered.length === 0 ? (
            <div className="blog-view-loading">
              {query ? `No posts matching "${query}".` : 'No blog posts yet.'}
            </div>
          ) : (
            filtered.map((post) => (
              <Link key={post.id} to={`/blog/${post.slug}`} className="bl-tile">
                {post.coverImage && (
                  <img src={post.coverImage} alt={post.title} className="bl-tile-img" />
                )}
                {(post.tags || []).length > 0 && (
                  <span className="bl-tile-tag">{post.tags[0]}</span>
                )}
                <h3 className="bl-tile-title">{post.title}</h3>
                {post.excerpt && <p className="bl-tile-excerpt">{post.excerpt}</p>}
                <div className="bl-tile-foot">
                  <span>{post.createdBy} · {fmtDate(post.publishedAt || post.createdAt)}</span>
                  <span>{readTime(post)}</span>
                </div>
              </Link>
            ))
          )}
        </div>

        <aside className="bl-side">
          {tags.length > 0 && (
            <div className="bl-scard">
              <h4>Browse Topics</h4>
              <div className="bv-tag-cloud">
                <Link to="/blog" className={`bv-tag ${!activeTag ? 'bv-tag-active' : ''}`}>All</Link>
                {tags.map((t) => (
                  <Link
                    key={t}
                    to={`/blog?tag=${encodeURIComponent(t)}`}
                    className={`bv-tag ${activeTag === t ? 'bv-tag-active' : ''}`}
                  >
                    {t}
                  </Link>
                ))}
              </div>
            </div>
          )}
          {recent.length > 0 && (
            <div className="bl-scard">
              <h4>Most Recent</h4>
              <div className="bl-recent">
                {recent.map((p) => (
                  <Link key={p.id} to={`/blog/${p.slug}`}>
                    {p.title}
                    <span className="d">{fmtDate(p.publishedAt || p.createdAt)}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default BlogList;
